import React from 'react'
import { Grid, Box } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { PromotedContentMainCard, PromotedContentSecondaryCard, PromotedContentGenericCard } from './../general-card/promoted-content-card'
import { useFormFactor, useGroup } from '../../hooks'
import { ALL_CONSUMER } from '../../services/cognito/userGroups'
import HeaderUnderline from '../header-underline'
import WithLoading from '../with-loading'

// PENG-3630: from md up the first item is a full-width hero and the other
// four sit in one row below as equal vertical cards. Every image is 16:9,
// so nothing is cropped and each card's height follows its image. The
// tighter column gap keeps the four reading as one set under the hero.
const useDesktopStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'grid',
    // minmax(0, …): a plain 1fr can't shrink below its content, so at
    // 900–1011px the cards' action rows pushed the grid past the page.
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gridTemplateRows: 'repeat(2, auto)',
    gridColumnGap: spacing(2),
    gridRowGap: spacing(3)
  },
  item1: { gridArea: '1 / 1 / 2 / 5' },
  item2: { gridArea: '2 / 1 / 3 / 2' },
  item3: { gridArea: '2 / 2 / 3 / 3' },
  item4: { gridArea: '2 / 3 / 3 / 4' },
  item5: { gridArea: '2 / 4 / 3 / 5' }
}))

// PENG-3630: the hero takes its own `auto` row; the four compact cards share
// `1fr` rows, which all size to the tallest of them, so they stay equal
// without stretching to half the hero's height.
const useMobileStyles = makeStyles(() => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto repeat(4, 1fr)',
    gridColumnGap: '24px',
    gridRowGap: '24px'
  },
  item1: { gridArea: '1 / 1 / 2 / 2' },
  item2: { gridArea: '2 / 1 / 3 / 2' },
  item3: { gridArea: '3 / 1 / 4 / 2' },
  item4: { gridArea: '4 / 1 / 5 / 2' },
  item5: { gridArea: '5 / 1 / 6 / 2' }
}))

const PromotedContent = ({ onCardSelect, onAddToPlaylist, promotedContent, isLoading }) => {
  const { isSmallTablet, isMobile } = useFormFactor()

  const desktopClasses = useDesktopStyles()
  const mobileClasses = useMobileStyles()

  const isSmallScreen = isSmallTablet || isMobile
  const classes = isSmallScreen ? mobileClasses : desktopClasses

  const { isInGroup } = useGroup()
  const isConsumer = isInGroup(ALL_CONSUMER)
  const title = 'Featured Content'

  const contentList = isLoading ? new Array(5).fill(null) : promotedContent

  const emptyPromotedContent = !promotedContent.length && !isLoading

  if (emptyPromotedContent) {
    return null
  }

  return (
    <Box>
      <Box mt={4} mb={2}>
        <WithLoading loading={isLoading}>
          <h1 style={isConsumer ? { marginBottom: 0 } : {}}>{title}</h1>
          {isConsumer && <HeaderUnderline width={64} color='primary' />}
        </WithLoading>
      </Box>
      <Grid container className={classes.root}>
        {contentList.map((item, index) => {
          const componentProps = {
            onSelect: onCardSelect && (() => onCardSelect(item?.content)),
            position: index,
            onAddToPlaylist,
            content: item?.content,
            size: index > 1 ? 'mobile' : 'large',
            testId: `promoted-content-card-${index}`
          }

          let component = <PromotedContentGenericCard {...componentProps} />

          if (index === 0) {
            component = <PromotedContentMainCard {...componentProps} />
          } else if (!isSmallScreen) {
            component = <PromotedContentSecondaryCard {...componentProps} />
          }

          return (
            <Grid
              item
              className={classes[`item${index + 1}`]}
              key={item?.content?.friendlyUrl ?? index}
            >
              {component}
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

export default PromotedContent
